/**
 * Intro animation sequence with reduced motion support
 */

import { prefersReducedMotion, isTouchDevice } from '../core/utils.js';

export class IntroAnimation {
  #overlay;
  #text;
  #line;
  #app;
  #onComplete;

  constructor(onComplete) {
    this.#overlay = document.getElementById('introOverlay');
    this.#text = document.getElementById('introText');
    this.#line = document.getElementById('introLine');
    this.#app = document.getElementById('app');
    this.#onComplete = onComplete;

    if (!this.#overlay) {
      onComplete?.();
      return;
    }
  }

  start() {
    // Skip if reduced motion or not visible
    if (prefersReducedMotion() || !this.#overlay || this.#overlay.offsetParent === null) {
      this.#skip();
      return;
    }

    // Animation sequence
    const sequence = [
      { action: () => this.#fadeInText(), delay: 300 },
      { action: () => this.#expandLine(), delay: 800 },
      { action: () => this.#exit(), delay: 2600 }
    ];

    let accumulatedDelay = 0;
    sequence.forEach(({ action, delay }) => {
      accumulatedDelay += delay - (sequence[sequence.indexOf({ action, delay }) - 1]?.delay || 0);
      setTimeout(action, accumulatedDelay);
    });
  }

  #fadeInText() {
    if (this.#text) {
      this.#text.style.transition = 'opacity 1s ease';
      this.#text.style.opacity = '1';
    }
  }

  #expandLine() {
    if (this.#line) {
      this.#line.style.transition = 'width 1.5s cubic-bezier(.23,1,.32,1)';
      this.#line.style.width = '240px';
    }
  }

  #exit() {
    if (this.#overlay) {
      this.#overlay.style.transition = 'opacity 1s ease, transform 1s ease';
      this.#overlay.style.opacity = '0';
      this.#overlay.style.transform = 'scale(1.1)';
    }

    if (this.#app) {
      this.#app.classList.add('loaded');
    }

    setTimeout(() => {
      if (this.#overlay) {
        this.#overlay.style.display = 'none';
        this.#overlay.setAttribute('aria-hidden', 'true');
      }
      this.#onComplete?.();
    }, 1000);
  }

  #skip() {
    if (this.#overlay) {
      this.#overlay.style.display = 'none';
      this.#overlay.setAttribute('aria-hidden', 'true');
    }
    if (this.#app) {
      this.#app.classList.add('loaded');
    }
    this.#onComplete?.();
  }
}

/**
 * Hero content reveal after intro
 */
export function revealHero() {
  const elements = [
    { id: 'navLogo', delay: 0 },
    { id: 'heroBadge', delay: 200 },
    { id: 'heroTitle', delay: 400 },
    { id: 'heroSub', delay: 600 },
    { id: 'heroCta', delay: 800 },
    { id: 'heroStats', delay: 1000 },
    { id: 'scrollIndicator', delay: 1500 }
  ];

  elements.forEach(({ id, delay }) => {
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => {
        el.classList.add('visible');
      }, delay);
    }
  });

  // Animate stats
  setTimeout(() => {
    document.querySelectorAll('.stat-number').forEach(el => {
      const target = parseInt(el.dataset.count) || 0;
      if (target === 0) {
        el.textContent = '0';
        return;
      }

      let current = 0;
      const duration = 1500;
      const steps = 60;
      const increment = target / steps;
      const stepDuration = duration / steps;

      const counter = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(counter);
        }
        el.textContent = Math.round(current);
      }, stepDuration);
    });
  }, 1200);
}
